import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { 
  CheckCircle, 
  XCircle,
  Sparkles,
  Ticket,
  ShoppingBag,
  Download,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState("loading");
  const [transaction, setTransaction] = useState(null);
  const [isAch, setIsAch] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [autoDownload, setAutoDownload] = useState({
    triggered: false,
    // For single-product transactions, url/filename is the one item.
    // For cart transactions, items[] holds every digital download.
    url: null,
    filename: null,
    error: null,
    items: [], // [{product_id, name, url, filename, error}]
  });

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  // Auto-trigger digital download(s) once the order is confirmed.
  // Handles both single-product transactions and multi-item cart transactions.
  useEffect(() => {
    if (status !== "success" || !transaction || !token || autoDownload.triggered) {
      return;
    }
    // Only fire for product-bearing transactions
    const isProduct = transaction.item_type === "product" && transaction.item_id;
    const isCart = transaction.item_type === "cart" && Array.isArray(transaction.metadata?.line_items);
    if (!isProduct && !isCart) return;

    let cancelled = false;

    const triggerAnchor = (url, filename) => {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "download";
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    const fetchOne = async (productId, productName) => {
      try {
        const res = await axios.get(
          `${API}/products/${productId}/download`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return {
          product_id: productId,
          name: productName,
          url: res.data?.download_url || null,
          filename: res.data?.filename || null,
          error: res.data?.download_url ? null : "no_url",
        };
      } catch (e) {
        const code = e.response?.status;
        return {
          product_id: productId,
          name: productName,
          url: null,
          filename: null,
          error: code === 404 ? "physical" : code === 403 ? "not_owned" : "fetch_failed",
        };
      }
    };

    (async () => {
      if (isProduct) {
        const r = await fetchOne(transaction.item_id, transaction.metadata?.product_name);
        if (cancelled) return;
        if (r.url) triggerAnchor(r.url, r.filename);
        setAutoDownload({ triggered: true, url: r.url, filename: r.filename, error: r.error, items: [r] });
        return;
      }
      // Cart mode: filter to items that DON'T require shipping (digital)
      const lineItems = transaction.metadata.line_items || [];
      const digitalItems = lineItems.filter((li) => !li.requires_shipping);
      if (digitalItems.length === 0) {
        // Cart has zero digital items — everything is physical
        if (cancelled) return;
        setAutoDownload({ triggered: true, url: null, filename: null, error: "physical", items: [] });
        return;
      }
      // Fetch all download URLs in parallel, then trigger anchor-clicks sequentially
      // with a small delay so the browser doesn't collapse them into one.
      const results = await Promise.all(
        digitalItems.map((li) => fetchOne(li.product_id, li.product_name))
      );
      if (cancelled) return;
      // Trigger downloads sequentially
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        if (r.url) {
          triggerAnchor(r.url, r.filename);
          if (i < results.length - 1) {
            await new Promise((res) => setTimeout(res, 500));
          }
        }
      }
      const first = results.find((r) => r.url) || results[0];
      setAutoDownload({
        triggered: true,
        url: first?.url || null,
        filename: first?.filename || null,
        error: first?.url ? null : first?.error || "fetch_failed",
        items: results,
      });
    })();
    return () => { cancelled = true; };
  }, [status, transaction, token, autoDownload.triggered]);

  const retryDownload = () => {
    // Cart: retry every failed item; single: retry the one item
    if (autoDownload.items && autoDownload.items.length > 1) {
      autoDownload.items.forEach((it, i) => {
        if (it.url) {
          setTimeout(() => {
            const a = document.createElement("a");
            a.href = it.url;
            a.download = it.filename || "download";
            a.rel = "noopener";
            a.target = "_blank";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          }, i * 500);
        }
      });
      return;
    }
    if (autoDownload.url) {
      const a = document.createElement("a");
      a.href = autoDownload.url;
      a.download = autoDownload.filename || "download";
      a.rel = "noopener";
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      setAutoDownload({ triggered: false, url: null, filename: null, error: null, items: [] });
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 8; // ACH-aware: 8 polls before showing "processing" message
    const pollInterval = 2000;

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data.is_ach) setIsAch(true);

      if (response.data.status === "paid") {
        setStatus("success");
        setTransaction(response.data.transaction);
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      if (attempts >= maxAttempts) {
        // ACH/crypto methods can take days to settle; show informative message instead of error
        setStatus(response.data.is_ach ? "processing_ach" : "timeout");
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (e) {
      console.error("Payment status check failed:", e);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="payment-success-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="pt-32 pb-16 px-6">
        <div className="max-w-lg mx-auto text-center">
          {status === "loading" && (
            <div className="card p-12" data-testid="loading-state">
              <Loader2 className="w-16 h-16 text-[#EC008C] mx-auto mb-6 animate-spin" />
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                Processing Payment
              </h1>
              <p className="text-[#8A92A0]">Please wait while we confirm your payment...</p>
            </div>
          )}

          {status === "success" && (
            <div className="card p-12" data-testid="success-state">
              <div className="w-20 h-20 rounded-full bg-[#8DB87A]/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#8DB87A]" />
              </div>
              <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">
                Payment Successful!
              </h1>
              <p className="text-[#8A92A0] mb-8">
                Thank you for your purchase. Your {transaction?.item_type === "ticket" ? "ticket(s)" : "order"} has been confirmed.
              </p>

              {transaction && (
                <div className="bg-[#0A0E18] rounded-lg p-6 mb-8 text-left">
                  <h3 className="font-semibold text-white mb-4">Order Details</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#8A92A0]">Item</span>
                      <span className="text-white">{transaction.metadata?.event_title || transaction.metadata?.product_name || transaction.item_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8A92A0]">Quantity</span>
                      <span className="text-white">{transaction.quantity || 1}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#8A92A0]">Total</span>
                      <span className="text-[#EC008C] font-semibold">${transaction.amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Auto-download banner — cart-aware. */}
              {(transaction?.item_type === "product" || transaction?.item_type === "cart") && autoDownload.triggered && autoDownload.url && (
                <div
                  className="bg-[#0A0E18] border border-[#8DB87A]/40 rounded-lg p-4 mb-6 text-left flex items-start gap-3"
                  data-testid="auto-download-banner"
                >
                  <Download className="w-5 h-5 text-[#8DB87A] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm mb-1">
                      {autoDownload.items && autoDownload.items.filter((i) => i.url).length > 1
                        ? `Downloading ${autoDownload.items.filter((i) => i.url).length} files…`
                        : "Your download has started"}
                    </p>
                    {autoDownload.items && autoDownload.items.length > 1 ? (
                      <ul className="text-[#8A92A0] text-xs mb-2 space-y-0.5 max-h-24 overflow-y-auto">
                        {autoDownload.items.map((it) => (
                          <li key={it.product_id} className="truncate">
                            {it.url ? "✓" : "•"} {it.filename || it.name || it.product_id}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[#8A92A0] text-xs mb-2 truncate">
                        {autoDownload.filename}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={retryDownload}
                      className="text-[#EC008C] text-xs font-medium hover:underline"
                      data-testid="retry-download-btn"
                    >
                      Didn&apos;t download? Click here
                    </button>
                  </div>
                </div>
              )}
              {(transaction?.item_type === "product" || transaction?.item_type === "cart") && autoDownload.triggered && !autoDownload.url && autoDownload.error === "physical" && (
                <div
                  className="bg-[#0A0E18] border border-[#1A2030] rounded-lg p-4 mb-6 text-left"
                  data-testid="physical-order-banner"
                >
                  <p className="text-white text-sm font-semibold mb-1">Order confirmed</p>
                  <p className="text-[#8A92A0] text-xs">
                    The artist will ship this to your address on file. You&apos;ll get a tracking update in your admin inbox.
                  </p>
                </div>
              )}
              {(transaction?.item_type === "product" || transaction?.item_type === "cart") && autoDownload.triggered && !autoDownload.url && autoDownload.error && autoDownload.error !== "physical" && (
                <div
                  className="bg-[#0A0E18] border border-[#F87171]/40 rounded-lg p-4 mb-6 text-left"
                  data-testid="download-error-banner"
                >
                  <p className="text-white text-sm font-semibold mb-1">Download not ready yet</p>
                  <p className="text-[#8A92A0] text-xs mb-2">
                    Payment confirmed. Retry the download or grab it any time from My Purchases.
                  </p>
                  <button
                    type="button"
                    onClick={retryDownload}
                    className="text-[#EC008C] text-xs font-medium hover:underline"
                    data-testid="retry-download-btn"
                  >
                    Retry download
                  </button>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {transaction?.item_type === "ticket" && (
                  <Button
                    onClick={() => navigate("/my-tickets")}
                    className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                    data-testid="view-tickets-btn"
                  >
                    <Ticket className="w-4 h-4 mr-2" />
                    View My Tickets
                  </Button>
                )}
              {(transaction?.item_type === "product" || transaction?.item_type === "cart") && (
                <Button
                  onClick={() => navigate("/my-orders")}
                  className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                  data-testid="view-purchases-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  View My Purchases &amp; Download
                </Button>
              )}
                {transaction?.item_type === "subscription" && (
                  <Button
                    onClick={() => navigate("/dashboard/subscription")}
                    className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                    data-testid="view-subscription-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    View My Subscription
                  </Button>
                )}
                {transaction?.item_type === "experience" && (
                  <Button
                    onClick={() => navigate("/dashboard/bookings")}
                    className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                    data-testid="view-bookings-btn"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    View My Booking
                  </Button>
                )}
                <Button
                  onClick={() => navigate("/discover")}
                  variant="outline"
                  className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                  data-testid="continue-browsing-btn"
                >
                  Continue Browsing
                </Button>
              </div>
            </div>
          )}

          {status === "processing_ach" && (
            <div className="card p-12" data-testid="processing-ach-state">
              <div className="w-20 h-20 rounded-full bg-[#EC008C]/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#EC008C] animate-spin" />
              </div>
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                Payment is Processing
              </h1>
              <p className="text-[#8A92A0] mb-2">Bank transfers settle in 3-5 business days.</p>
              <p className="text-[#8A92A0] mb-8">We'll email you the moment it clears — your order activates automatically.</p>
              <Button
                onClick={() => navigate("/home")}
                className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div className="card p-12" data-testid="error-state">
              <div className="w-20 h-20 rounded-full bg-[#F87171]/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-[#F87171]" />
              </div>
              <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-2">
                {status === "timeout" ? "Payment Status Unknown" : "Payment Issue"}
              </h1>
              <p className="text-[#8A92A0] mb-8">
                {status === "timeout" 
                  ? "We couldn't confirm your payment status. Please check your email for confirmation."
                  : status === "expired"
                  ? "This payment session has expired."
                  : "There was an issue processing your payment. Please try again."}
              </p>
              <Button
                onClick={() => navigate("/discover")}
                className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399]"
              >
                Back to Discover
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default PaymentSuccess;
